import { api } from '@/lib/api.js';
import { inFlightDedup } from '@/lib/inFlight.js';

// Activity timeline reads (legacy) + Comms Hub send flow.
//
// HTTP contracts verified against routes/tracon-send.routes.js
// (V1.B Step 7, 2026-05-19):
//   GET  /:token/templates      → { success, templates: [{ slug, label,
//                                   category, audience, toBroker, needsExtra }] }
//   GET  /:token/preview/:slug  → { success, slug, html, subject, to, toName,
//                                   cc, bcc, contact_crm_id, deal_id,
//                                   override_active, override_address }
//   POST /:token/send           → body: { slug, to, subject, toName, cc, bcc,
//                                   urgency, ai_name, ai_email,
//                                   pilot_crm_id }
//                                 returns: { success, mode, to, subject }
//
// :token is the Supabase session token (sessions.token).

// ─── Activity / comms timeline (legacy reads) ─────────────────────

export function getActivityForDeal(dealId, { limit = 100 } = {}) {
  return inFlightDedup(`activity:${dealId}:${limit}`, () =>
    api.get(
      `/api/tracon/activity/${encodeURIComponent(dealId)}?limit=${encodeURIComponent(limit)}`
    )
  );
}

// ─── Comms Hub send flow ──────────────────────────────────────────

export function getSendTemplates(sessionToken) {
  return inFlightDedup(`send-templates:${sessionToken}`, () =>
    api.get(`/api/tracon/send/${encodeURIComponent(sessionToken)}/templates`)
  );
}

export function previewTemplate(sessionToken, slug, extra = {}) {
  const qs = new URLSearchParams();
  if (extra.urgency) qs.set('urgency', extra.urgency);
  if (extra.ai_name) qs.set('ai_name', extra.ai_name);
  if (extra.ai_email) qs.set('ai_email', extra.ai_email);
  if (extra.pilot_id) qs.set('pilot_id', extra.pilot_id);
  if (extra.quote_id) qs.set('quote_id', extra.quote_id);
  // No send escapes EMAIL_OVERRIDE (2026-10-14), so the preview reports it
  // whenever it is set; there is no live flag to pass.
  const q = qs.toString();
  return api.get(
    `/api/tracon/send/${encodeURIComponent(sessionToken)}/preview/${encodeURIComponent(slug)}${q ? '?' + q : ''}`
  );
}

export function sendTemplate(sessionToken, payload) {
  return api.post(
    `/api/tracon/send/${encodeURIComponent(sessionToken)}/send`,
    payload
  );
}

// ─── Deal-keyed (session-optional) send ───────────────────────────
// A broker email does not require a client session. These hit the
// deal-keyed twins of the routes above (routes/tracon-send.routes.js
// /deal/:dealId/*), which render the chosen template from the CRM Deal
// and send via sendCRMEmail + log to the deal. Only session-optional
// templates (no client tower link) are offered; the backend 400s the
// rest with a "Pre-Stage the deal" message.

export function getSendTemplatesForDeal(dealId) {
  return inFlightDedup(`send-templates-deal:${dealId}`, () =>
    api.get(`/api/tracon/send/deal/${encodeURIComponent(dealId)}/templates`)
  );
}

export function previewTemplateForDeal(dealId, slug, extra = {}) {
  const qs = new URLSearchParams();
  if (extra.urgency) qs.set('urgency', extra.urgency);
  if (extra.ai_name) qs.set('ai_name', extra.ai_name);
  if (extra.ai_email) qs.set('ai_email', extra.ai_email);
  if (extra.pilot_id) qs.set('pilot_id', extra.pilot_id);
  if (extra.quote_id) qs.set('quote_id', extra.quote_id);
  const q = qs.toString();
  return api.get(
    `/api/tracon/send/deal/${encodeURIComponent(dealId)}/preview/${encodeURIComponent(slug)}${q ? '?' + q : ''}`
  );
}

export function sendTemplateForDeal(dealId, payload) {
  return api.post(
    `/api/tracon/send/deal/${encodeURIComponent(dealId)}/send`,
    payload
  );
}

// ─── SMS (Gap 2) ──────────────────────────────────────────────────
// Ad-hoc / account-level texts. Sends + logs to the communications
// timeline server-side. Routes (broker JWT, no path token):
//   POST /api/tracon/comms/send-sms
//   GET  /api/tracon/comms/resolve-phone
// Per-pilot reminders use sendPilotNudge() instead (canonical body + log).

export function sendSMS({ phone, message, crm_contact_id, crm_deal_id, contact_name } = {}) {
  return api.post('/api/tracon/comms/send-sms', {
    phone,
    message,
    crm_contact_id: crm_contact_id || null,
    crm_deal_id: crm_deal_id || null,
    contact_name: contact_name || null,
  });
}

// Resolve an E.164 mobile for SMS mode. Returns { phone, opted_out }.
export function resolvePhone({ crm_contact_id, pilot_id } = {}) {
  const qs = new URLSearchParams();
  if (crm_contact_id) qs.set('crm_contact_id', crm_contact_id);
  if (pilot_id) qs.set('pilot_id', pilot_id);
  return api.get(`/api/tracon/comms/resolve-phone?${qs.toString()}`);
}

// Per-pilot reminder over a chosen channel. The backend owns the canonical
// SMS body + logging (routes/tracon.routes.js /nudge).
export function sendPilotNudge(sessionToken, pilotId, { channel, note } = {}) {
  return api.post(
    `/api/tracon/session/${encodeURIComponent(sessionToken)}/pilot/${encodeURIComponent(pilotId)}/nudge`,
    { channel, note: note || null }
  );
}
